import { BehaviorSubject } from 'rxjs';
/**
 * Globally accessible information of FeatureAppModel state
 */
class FeatureAppModel {
    /**
     * Feature App Name
     */
    name;
    /**
     * Page route
     */
    route;
    /**
     * Manual id
     */
    id;
    /**
     * Metadata useful for identification and search.
     */
    meta = {};
    /**
     * Tags for querying
     */
    _tags;
    /**
     * Get tags
     */
    get tags() {
        return this._tags;
    }
    /**
     * Set tags.
     * Ensure they are an array
     */
    set tags(tags) {
        this._tags = Array.isArray(tags) ? tags : [];
    }
    /**
     * Access tokens, e.g. for API access
     * Useful mostly for containers like the root doc/integrator
     */
    get tokens() {
        return this._tokens;
    }
    /**
     * Only store tokens that are defined (truthy)
     */
    set tokens(tokens) {
        this._tokens = Object.entries(tokens).reduce((accumulator, [key, value]) => {
            if (!!key && !!value) {
                accumulator[key] = value;
            }
            return accumulator;
        }, {});
    }
    /**
     * Private tokens store
     */
    _tokens = {};
    /**
     * Access Contentful config variables, e.g. for API access
     * Useful mostly for containers like PWA
     */
    get config() {
        return this._config;
    }
    /**
     * Only store config variables that are defined (truthy)
     */
    set config(config) {
        if (!config)
            return;
        this._config = Object.entries(config).reduce((accumulator, [key, value]) => {
            if (!!key && !!value) {
                accumulator[key] = value;
            }
            return accumulator;
        }, {});
    }
    /**
     * Private contentful config store
     */
    _config = {};
    /**
     * Content Data
     */
    get data() {
        return this._dataSubject?.value;
    }
    set data(data) {
        // Protect data from accidental changes
        this._dataSubject.next(Object.freeze(data));
    }
    _dataSubject;
    /**
     * Instantiate a Feature App with metadata and content data.
     * @param input Some of the readonly properties
     * @param data Initial data
     */
    constructor(input, data) {
        // Create a new observer
        this._dataSubject = new BehaviorSubject(data);
        // Assign readonly values that have been provided to the constructor
        for (const key in input) {
            const objKey = key;
            this[objKey] = input[objKey];
        }
        // Make sure we use the current route
        if (document?.location?.pathname) {
            this.route = document.location.pathname;
        }
    }
    /**
     * Subscribe to changes of Feature App data.
     * Return function for unsubscribing
     * @param callback Event callback function
     */
    subscribe(callback) {
        const subscription = this._dataSubject.subscribe(callback);
        return () => subscription.unsubscribe();
    }
    actions = new Map();
    registerAction(actionName, callback) {
        this.actions.set(actionName, callback);
        return () => this.unRegisterAction(actionName);
    }
    unRegisterAction(actionName) {
        this.actions.delete(actionName);
    }
    unRegisterAllActions() {
        this.actions.clear();
    }
    dispatch(actionName, ...args) {
        const actionFn = this.actions.get(actionName);
        if (typeof actionFn === 'function') {
            return actionFn(...args);
        }
        if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
            console.warn(`Action ${actionName} could not be executed in Feature App ${this.name}`);
            console.log(`Action ${actionName} was fired with the following arguments:`, ...args);
        }
    }
}
export default FeatureAppModel;
